import { type SceneDocument } from '@/domain/model';

/**
 * Where documents live.
 *
 * Declared by the application, implemented by infrastructure — dependency inversion, and
 * the reason swapping browser storage for a REST backend is a one-line change in the
 * composition root rather than an edit to every caller.
 *
 * Everything is async because every real implementation is: IndexedDB, `fetch`, and the
 * File System Access API all return promises. Making the in-memory test double async too
 * keeps the interface honest rather than letting synchronous assumptions leak into
 * callers that would then break against the real thing.
 */

export interface DocumentSummary {
  readonly id: string;
  readonly title: string;
  /** Milliseconds since the epoch. Supplied by the repository, not by the domain. */
  readonly updatedAt: number;
  /** Opaque token for optimistic concurrency; `null` when the store has no notion of it. */
  readonly revision: string | null;
}

export interface StoredDocument {
  readonly summary: DocumentSummary;
  readonly document: SceneDocument;
  /** Fields written by a newer build, carried through so nothing is lost on re-save. */
  readonly unknown: Readonly<Record<string, unknown>>;
  /** Problems found while loading — dropped entities, applied migrations. */
  readonly warnings: readonly string[];
}

export interface SaveOptions {
  /**
   * The revision the caller believes it is replacing.
   *
   * A repository that supports it rejects the write when the stored revision differs,
   * which is what stops an autosave from a stale tab silently overwriting work done in
   * another one.
   */
  readonly expectedRevision?: string | null | undefined;
  readonly unknown?: Readonly<Record<string, unknown>> | undefined;
}

/** Thrown when `expectedRevision` does not match what is stored. */
export class RevisionConflictError extends Error {
  readonly storedRevision: string | null;

  constructor(storedRevision: string | null) {
    super('the document was changed elsewhere since it was loaded');
    this.name = 'RevisionConflictError';
    this.storedRevision = storedRevision;
  }
}

export interface DocumentRepository {
  list(): Promise<readonly DocumentSummary[]>;
  load(id: string): Promise<StoredDocument | null>;
  save(id: string, document: SceneDocument, options?: SaveOptions): Promise<DocumentSummary>;
  remove(id: string): Promise<void>;
}
